import { CaretDown, CaretRight, Eye, CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";
import type { ExistingResponseInfo } from "../types";

interface ExistingAnswerDetailsProps {
  existingResponse: ExistingResponseInfo;
  conditionKey: string;
  isExpanded: boolean;
  onToggleExpand: (key: string) => void;
}

export function ExistingAnswerDetails({
  existingResponse,
  conditionKey,
  isExpanded,
  onToggleExpand,
}: ExistingAnswerDetailsProps) {
  if (!existingResponse.hasExisting) return null;

  return (
    <div className="mb-2">
      <button
        onClick={() => onToggleExpand(conditionKey)}
        className="flex items-center gap-1 text-[10px] text-amber-700 hover:text-amber-800 transition-colors"
      >
        {isExpanded ? (
          <CaretDown className="h-3 w-3" weight="bold" />
        ) : (
          <CaretRight className="h-3 w-3" weight="bold" />
        )}
        <Eye className="h-3 w-3" weight="duotone" />
        <span>Bekijk bestaand antwoord</span>
      </button>

      {isExpanded && (
        <div className="mt-2 p-2 bg-amber-50/50 rounded border border-amber-200">
          <div className="space-y-1.5 text-[10px]">
            <div className="flex items-start gap-2">
              <span className="text-neutral-500 min-w-[80px]">Huidig antwoord:</span>
              <span className="font-medium text-neutral-700">
                {existingResponse.selectedOptionLabel ||
                  existingResponse.freeTextValue ||
                  '-'}
              </span>
            </div>

            <div className="flex items-start gap-2">
              <span className="text-neutral-500 min-w-[80px]">Bron:</span>
              <span className="text-neutral-700">
                {existingResponse.sourceTypeLabelNl || existingResponse.sourceType}
              </span>
            </div>

            {existingResponse.sourceFileName && (
              <div className="flex items-start gap-2">
                <span className="text-neutral-500 min-w-[80px]">Bronbestand:</span>
                {existingResponse.sourceFileUrl ? (
                  <a
                    href={existingResponse.sourceFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {existingResponse.sourceFileLabel || existingResponse.sourceFileName}
                    <ArrowSquareOut className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-neutral-700">
                    {existingResponse.sourceFileLabel || existingResponse.sourceFileName}
                  </span>
                )}
              </div>
            )}

            {existingResponse.updatedAt && (
              <div className="flex items-start gap-2">
                <span className="text-neutral-500 min-w-[80px]">Laatst bijgewerkt:</span>
                <span className="text-neutral-700">
                  {new Date(existingResponse.updatedAt).toLocaleDateString('nl-BE', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}

            {existingResponse.validatedBy && (
              <div className="flex items-start gap-2">
                <span className="text-neutral-500 min-w-[80px]">Bevestigd door:</span>
                <span className="text-green-700 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" weight="fill" />
                  {existingResponse.validatedByName || existingResponse.validatedBy}
                  {existingResponse.validatedAt && (
                    <span className="text-neutral-500">
                      ({new Date(existingResponse.validatedAt).toLocaleDateString('nl-BE')})
                    </span>
                  )}
                </span>
              </div>
            )}

            {existingResponse.notes && (
              <div className="flex items-start gap-2">
                <span className="text-neutral-500 min-w-[80px]">Notities:</span>
                <span className="text-neutral-700 italic">
                  {existingResponse.notes}
                </span>
              </div>
            )}
          </div>

          <p className="mt-2 text-[9px] text-amber-600 italic">
            Als u opslaat wordt het bestaande antwoord overschreven
          </p>
        </div>
      )}
    </div>
  );
}
